import React, { useContext, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    Typography,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { MuiFileInput } from "mui-file-input";

import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";

import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import DataTable from "../../components/DataTable";
import { LanguageContext } from "../../components/LanguageContext";
import { getCalculations, postCalculation } from "../../api/calculationApi";

const ActionCard = ({ to, icon, title, description }) => (
    <SectionCard
        sx={{
            cursor: "pointer",
            transition: "border-color 0.2s ease",
            "&:hover": { borderColor: "primary.main" },
        }}
        padded={false}
    >
        <Box
            component={RouterLink}
            to={to}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2.5,
                textDecoration: "none",
                color: "inherit",
            }}
        >
            <Box
                sx={(theme) => ({
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor:
                        theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(15,23,42,0.04)",
                    color: "primary.main",
                    flexShrink: 0,
                })}
            >
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
                {description && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {description}
                    </Typography>
                )}
            </Box>
            <ChevronRightRoundedIcon sx={{ color: "text.secondary" }} />
        </Box>
    </SectionCard>
);

const SingleStorePage = () => {
    const { translations } = useContext(LanguageContext) || {};
    const { storeCode } = useParams();
    const queryClient = useQueryClient();

    const [open, setOpen] = useState(false);
    const [excel, setExcel] = useState(null);
    const [excelError, setExcelError] = useState("");
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    const { data: calculations, isLoading } = useQuery({
        queryKey: ["calculations", storeCode],
        queryFn: () => getCalculations(storeCode),
    });

    const mutation = useMutation({
        mutationFn: (formData) => postCalculation(formData),
        onSuccess: () => {
            setSuccess(
                translations?.insertExcelSuccess ||
                    "Excel je uspešno obrađen!"
            );
            setError("");
            setOpen(false);
            setExcel(null);
            queryClient.invalidateQueries(["calculations", storeCode]);
        },
        onError: () => {
            setError(
                translations?.insertExcelError ||
                    "Greška prilikom obrade Excel fajla."
            );
            setSuccess("");
        },
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!excel) {
            setExcelError(
                translations?.adminSingleStorePageFileError ||
                    "Izaberite Excel fajl!"
            );
            return;
        }
        setExcelError("");
        const formData = new FormData();
        formData.append("excel", excel, excel.name);
        formData.append("store_code", storeCode);
        formData.append("user_id", localStorage.getItem("user_id"));
        mutation.mutateAsync(formData).catch(() => {});
    };

    const columns = useMemo(
        () => [
            {
                field: "id",
                headerName: translations?.adminSingleStorePageId || "ID",
                width: 70,
                align: "center",
                headerAlign: "center",
                renderCell: (p) => (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace" }}
                    >
                        {p.value}
                    </Typography>
                ),
            },
            {
                field: "file_name",
                headerName:
                    translations?.adminSingleStorePageFileName || "Naziv fajla",
                minWidth: 280,
                flex: 2,
                renderCell: (p) => (
                    <Typography
                        sx={{
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                        title={p.value}
                    >
                        {p.value}
                    </Typography>
                ),
            },
            {
                field: "s_name",
                headerName:
                    translations?.adminSingleStorePageStoreName || "Prodavnica",
                minWidth: 160,
                flex: 1,
            },
            {
                field: "date_time",
                headerName:
                    translations?.adminSingleStorePageTime || "Vreme",
                minWidth: 180,
                renderCell: (p) => (
                    <Typography variant="body2" color="text.secondary">
                        {p.value}
                    </Typography>
                ),
            },
            {
                field: "info",
                headerName:
                    translations?.adminSingleStorePageInfo || "Detalji",
                minWidth: 140,
                sortable: false,
                filterable: false,
                align: "center",
                headerAlign: "center",
                renderCell: (p) => (
                    <Button
                        component={RouterLink}
                        to={`/prodavnica/${storeCode}/informacije/${p.row.id}`}
                        variant="text"
                        size="small"
                        startIcon={<InfoOutlinedIcon />}
                    >
                        {translations?.adminSingleStorePageInfo || "Detalji"}
                    </Button>
                ),
            },
        ],
        [translations, storeCode]
    );

    return (
        <Box>
            <PageHeader
                icon={<StorefrontOutlinedIcon />}
                eyebrow={translations?.store || "Prodavnica"}
                title={`${translations?.store || "Prodavnica"} ${storeCode}`}
                description={
                    translations?.singleStoreSubtitle ||
                    "Pregled podataka, kalkulacije, narudžbenice i izveštaji."
                }
                actions={
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => setOpen(true)}
                        startIcon={<CalculateOutlinedIcon />}
                    >
                        {translations?.adminSingleStorePageNewCalculation ||
                            "Nova kalkulacija"}
                    </Button>
                }
            />

            <Stack spacing={3}>
                {success && (
                    <Alert severity="success" onClose={() => setSuccess("")}>
                        {success}
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <ActionCard
                            to={`/prodavnica/${storeCode}/narudzbine`}
                            icon={<FolderOpenOutlinedIcon />}
                            title={
                                translations?.createdOrders ||
                                "Kreirane narudžbenice"
                            }
                            description="Pregled svih narudžbenica"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <ActionCard
                            to={`/prodavnica/${storeCode}/izvestaji`}
                            icon={<AssessmentOutlinedIcon />}
                            title={translations?.reports || "Izveštaji"}
                            description="Generiši izveštaje"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <ActionCard
                            to={`/prodavnica/${storeCode}/proizvodi`}
                            icon={<Inventory2OutlinedIcon />}
                            title={
                                translations?.adminSingleStorePageProducts ||
                                "Proizvodi"
                            }
                            description="Katalog proizvoda"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <ActionCard
                            to={`/prodavnica/${storeCode}/lager`}
                            icon={<WarehouseOutlinedIcon />}
                            title={
                                translations?.adminAdministratorMenuWarehouse ||
                                "Lager"
                            }
                            description="Trenutno stanje magacina"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <ActionCard
                            to={`/prodavnica/${storeCode}/istorijat`}
                            icon={<HistoryOutlinedIcon />}
                            title={
                                translations?.adminSingleStorePageHistory ||
                                "Istorijat"
                            }
                            description="Istorija prodaje"
                        />
                    </Grid>
                </Grid>

                <SectionCard
                    title={
                        translations?.calculationsHistory ||
                        "Istorija kalkulacija"
                    }
                    description="Pregled svih obrada za ovu prodavnicu."
                    padded={false}
                >
                    <DataTable
                        rows={calculations?.data || []}
                        columns={columns}
                        loading={isLoading}
                        rowHeight={60}
                        emptyIcon={<CalculateOutlinedIcon />}
                        emptyTitle={
                            translations?.noCalculationsTitle ||
                            "Nema kalkulacija"
                        }
                        emptyDescription={
                            translations?.noCalculationsDescription ||
                            "Pokreni prvu kalkulaciju klikom na Nova kalkulacija."
                        }
                    />
                </SectionCard>
            </Stack>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    <CalculateOutlinedIcon color="primary" />
                    {translations?.adminSingleStorePageCalculate ||
                        "Nova kalkulacija"}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {excelError && (
                            <Alert
                                severity="error"
                                onClose={() => setExcelError("")}
                            >
                                {excelError}
                            </Alert>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            Odaberi Excel fajl sa prognozom. Sistem će izračunati
                            ABC-VED, ROP i predlog nabavke.
                        </Typography>
                        <MuiFileInput
                            value={excel}
                            onChange={setExcel}
                            label={
                                translations?.adminSingleStorePageSelectFile ||
                                "Izaberi fajl"
                            }
                            inputProps={{ accept: ".xls, .xlsx, .csv" }}
                            clearIconButtonProps={{
                                title: "Obriši",
                                children: <CloseRoundedIcon fontSize="small" />,
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setOpen(false)}>
                        {translations?.adminNo || "Otkaži"}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={mutation.isLoading}
                        startIcon={
                            mutation.isLoading ? (
                                <CircularProgress
                                    size={18}
                                    color="inherit"
                                />
                            ) : (
                                <UploadFileRoundedIcon />
                            )
                        }
                    >
                        {translations?.adminSingleStorePageCalculate ||
                            "Pokreni kalkulaciju"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SingleStorePage;